import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../translations';
import {
  PanelContainer, Title, Subtitle, PrimaryButton, SecondaryButton,
  PageHeader, HeaderActions, TitleRow, IconButton, ToggleSwitch,
} from '../ui/common';
import { ArrowLeftIcon } from '../ui/icons';
import { API_BASE } from '../config';
import {
  PRICE_PRESETS, presetRulePayload, parseCustomPercent, presetDirection, MIN_PROCENT_REGULY,
} from '../utils/pricePresets';
import { useConfirm } from '../components/ui/Modal';
import SimpleSelect from '../ui/SimpleSelect';
import { TylkoOdczytWPrzerwie } from '../components/przerwa/KontoWPrzerwie';

const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7];

const emptyForm = {
  name: '',
  active: true,
  date_from: '',
  date_to: '',
  weekdays: [],
  class_id: '',
  type_id: '',
  adjustment_type: 'percent',
  adjustment_value: '',
  priority: 0,
  // 070: czego dotyczy reguła ('all' | 'equipment' | 'classes' — rezerwacja według klas w widgecie).
  applies_to: 'all',
  // N.2: reguła działa też na pakiety.
  includes_bundles: false,
};

// N.2: na pakiety działa tylko reguła procentowa bez typu i klasy (lib/bundles.js#ruleAppliesToBundle).
function moznaNaPakiety(form) {
  return form.adjustment_type === 'percent' && !form.class_id && !form.type_id;
}

function csrfHeaders() {
  return {
    'Content-Type': 'application/json',
    'X-CSRF-Token': localStorage.getItem('csrfToken') || '',
  };
}

function ruleToForm(rule) {
  return {
    name: rule.name || '',
    active: rule.active !== false,
    date_from: rule.dateFrom || '',
    date_to: rule.dateTo || '',
    weekdays: Array.isArray(rule.weekdays) ? rule.weekdays : [],
    class_id: rule.classId != null ? String(rule.classId) : '',
    type_id: rule.typeId != null ? String(rule.typeId) : '',
    adjustment_type: rule.adjustmentType || 'percent',
    adjustment_value: rule.adjustmentValue != null ? String(rule.adjustmentValue) : '',
    priority: rule.priority || 0,
    applies_to: rule.appliesTo || 'all',
    includes_bundles: rule.includesBundles === true,
  };
}

// PUT /api/price-rules/:id wymaga pełnego payloadu — odtwarzamy go z reguły
// i nadpisujemy tylko to, co zmienia toggle/chip (P7.6). Bez `includes_bundles`: PUT bez tego pola
// zostawia zapisaną wartość, więc toggle i chip nie zmieniają flagi. Przełącznik podaje ją wprost.
function ruleToPayload(rule, overrides = {}) {
  return {
    name: rule.name || '',
    active: rule.active !== false,
    date_from: rule.dateFrom || null,
    date_to: rule.dateTo || null,
    weekdays: Array.isArray(rule.weekdays) && rule.weekdays.length ? rule.weekdays : null,
    class_id: rule.classId != null ? rule.classId : null,
    type_id: rule.typeId != null ? rule.typeId : null,
    adjustment_type: rule.adjustmentType || 'percent',
    adjustment_value: rule.adjustmentValue,
    priority: rule.priority || 0,
    preset_key: rule.presetKey || null,
    applies_to: rule.appliesTo || 'all',
    ...overrides,
  };
}

function formatSignedPercent(value) {
  return `${value > 0 ? '+' : ''}${String(value).replace('.', ',')}%`;
}

// 'YYYY-MM-DD' (wartość <input type="date">) -> 'DD.MM', do zwięzłego statusu presetu.
function formatDdMm(iso) {
  if (!iso) return '';
  const [, m, d] = iso.split('-');
  return `${d}.${m}`;
}

export default function PriceRules() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const confirm = useConfirm();

  const [rules, setRules] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [classes, setClasses] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const formRef = useRef(null);

  // Formularz renderuje się PO "Szybkich regułach" i "Sugestiach cenowych" — przy kilku
  // sugestiach potrafi wylądować kawałek poniżej ekranu, więc "Nowa reguła"/"Edytuj"/
  // zatwierdzenie sugestii (wszystkie trzy przełączają ten sam formOpen) muszą same
  // przewinąć do niego widok, inaczej trzeba go szukać scrollowaniem.
  useEffect(() => {
    if (formOpen && formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [formOpen]);

  // P7.6: „Szybkie reguły" — klucz presetu w trakcie zapisu, błąd zapisu i
  // lokalny wybór chipa % (zanim reguła presetu powstanie / gdy jest wyłączona).
  const [presetSaving, setPresetSaving] = useState(null);
  const [presetError, setPresetError] = useState('');
  const [presetChips, setPresetChips] = useState({});
  // Tekst wpisywany w pole „własny” procent, per preset, do chwili zatwierdzenia (Enter albo
  // wyjście z pola). Brak wpisu = pole pokazuje bieżącą wartość, gdy nie jest żadnym z chipów.
  const [presetCustom, setPresetCustom] = useState({});
  // Zakres dat presetów sezonowych — EDYTOWALNY, bo "sezon wysoki" narciarni to
  // grudzień–marzec, ale rowerowni czy kajakarni to czerwiec–sierpień. Klucz per
  // preset.key, nadpisuje wartość wyliczoną z rule/sugestię startową (patrz presetDateRange).
  const [presetDates, setPresetDates] = useState({});
  // N.2: „działa też na pakiety” wybrane, zanim reguła presetu powstała. Istniejąca reguła trzyma
  // to na serwerze, a brak wpisu tutaj znaczy, że użytkownik przełącznika nie dotknął.
  const [presetBundles, setPresetBundles] = useState({});

  const loadRules = () => {
    setLoading(true);
    axios.get(`${API_BASE}/api/price-rules`, { withCredentials: true })
      .then((res) => { setRules(Array.isArray(res.data) ? res.data : []); setError(''); })
      .catch(() => setError(t('priceRules.loadError')))
      .finally(() => setLoading(false));
    // Suggestions are advisory — a failed fetch just hides the panel.
    axios.get(`${API_BASE}/api/price-suggestions`, { withCredentials: true })
      .then((res) => setSuggestions(Array.isArray(res.data?.suggestions) ? res.data.suggestions : []))
      .catch(() => setSuggestions([]));
  };

  useEffect(() => {
    loadRules();
    axios.get(`${API_BASE}/api/classes`, { withCredentials: true })
      .then((res) => setClasses(Array.isArray(res.data) ? res.data : []))
      .catch(() => setClasses([]));
    axios.get(`${API_BASE}/api/types`, { withCredentials: true })
      .then((res) => setTypes(Array.isArray(res.data) ? res.data : []))
      .catch(() => setTypes([]));
  }, []);

  const classById = useMemo(() => {
    const map = {};
    classes.forEach((c) => { map[c.id] = c.name; });
    return map;
  }, [classes]);

  const typeById = useMemo(() => {
    const map = {};
    types.forEach((tp) => { map[tp.id] = tp.name; });
    return map;
  }, [types]);

  const ruleByPreset = useMemo(() => {
    const map = {};
    rules.forEach((rule) => { if (rule.presetKey) map[rule.presetKey] = rule; });
    return map;
  }, [rules]);

  // Chip pokazywany przy presecie: lokalny wybór > wartość istniejącej reguły > domyślny.
  const presetChipValue = (preset) => {
    if (presetChips[preset.key] != null) return presetChips[preset.key];
    const rule = ruleByPreset[preset.key];
    return rule ? Number(rule.adjustmentValue) : preset.defaultChip;
  };

  // Zakres dat presetu: lokalna edycja > PRAWDA z zapisanej reguły > podpowiedź startowa
  // (preset.dates policzone od dziś — tylko po to, żeby pola nie były puste przy pierwszym
  // użyciu). Ta sama kolejność co presetChipValue wyżej, celowo — jeden wzorzec priorytetu.
  const presetDateRange = (preset) => {
    if (presetDates[preset.key]) return presetDates[preset.key];
    const rule = ruleByPreset[preset.key];
    if (rule) return { from: rule.dateFrom || '', to: rule.dateTo || '' };
    if (!preset.dates) return { from: '', to: '' };
    const r = preset.dates(new Date());
    return { from: r.date_from || '', to: r.date_to || '' };
  };

  const presetIncludesBundles = (preset) => {
    const rule = ruleByPreset[preset.key];
    return rule ? rule.includesBundles === true : presetBundles[preset.key] === true;
  };

  const handlePresetBundles = (preset) => {
    setPresetError('');
    const rule = ruleByPreset[preset.key];
    if (!rule) {
      // Reguły jeszcze nie ma: wybór idzie z pierwszym włączeniem presetu.
      setPresetBundles((prev) => ({ ...prev, [preset.key]: prev[preset.key] !== true }));
      return undefined;
    }
    return savePreset(preset, async () => {
      await axios.put(`${API_BASE}/api/price-rules/${rule.id}`, ruleToPayload(rule, {
        includes_bundles: rule.includesBundles !== true,
      }), { withCredentials: true, headers: csrfHeaders() });
    });
  };

  const handlePresetDateChange = (preset, field, value) => {
    const next = { ...presetDateRange(preset), [field]: value };
    setPresetDates((prev) => ({ ...prev, [preset.key]: next }));
    setPresetError('');

    // Preset już aktywny: zmiana dat obowiązuje od razu (ten sam wzorzec co zmiana
    // chipa %-owego w handlePresetChip) — nie czekamy na osobny przycisk "zapisz".
    const rule = ruleByPreset[preset.key];
    if (rule && rule.active !== false && next.from && next.to) {
      if (next.from > next.to) { setPresetError(t('priceRules.dateRangeInvalid')); return; }
      savePreset(preset, async () => {
        await axios.put(`${API_BASE}/api/price-rules/${rule.id}`, ruleToPayload(rule, {
          date_from: next.from, date_to: next.to,
        }), { withCredentials: true, headers: csrfHeaders() });
      });
    }
  };

  const savePreset = async (preset, request) => {
    setPresetError('');
    setPresetSaving(preset.key);
    try {
      await request();
      loadRules();
    } catch (err) {
      setPresetError(err?.response?.data?.message || t('priceRules.presets.saveError'));
    } finally {
      setPresetSaving(null);
    }
  };

  const handlePresetToggle = (preset) => {
    const rule = ruleByPreset[preset.key];
    const isOn = !!rule && rule.active !== false;
    const chipValue = presetChipValue(preset);
    const range = presetDateRange(preset);

    // Włączanie presetu z datami (sezon wysoki/poza sezonem) bez ustawionego zakresu
    // nie ma sensu — to admin decyduje, kiedy jest jego sezon, więc blokujemy zamiast
    // cicho zgadywać (kiedyś: zawsze zima). Wyłączanie nigdy nie jest blokowane.
    if (!isOn && preset.hasEditableDates) {
      if (!range.from || !range.to) { setPresetError(t('priceRules.presets.dateRangeRequired')); return undefined; }
      if (range.from > range.to) { setPresetError(t('priceRules.dateRangeInvalid')); return undefined; }
    }

    return savePreset(preset, async () => {
      if (!rule) {
        await axios.post(`${API_BASE}/api/price-rules`, presetRulePayload(preset.key, chipValue, {
          dateFrom: range.from, dateTo: range.to, includesBundles: presetBundles[preset.key],
        }), { withCredentials: true, headers: csrfHeaders() });
      } else if (isOn) {
        // Wyłączenie: tylko active=false — reguła zostaje w tabeli, NIGDY DELETE.
        await axios.put(`${API_BASE}/api/price-rules/${rule.id}`, ruleToPayload(rule, { active: false }), {
          withCredentials: true, headers: csrfHeaders(),
        });
      } else {
        // Ponowne włączenie: zakres dat z pól (edytowalny przez admina, patrz
        // presetDateRange) + aktualnie wybrany chip. Nie przeliczamy automatycznie —
        // to był stary sezon narciarski zaszyty w kodzie.
        await axios.put(`${API_BASE}/api/price-rules/${rule.id}`, ruleToPayload(rule, {
          active: true,
          adjustment_value: chipValue,
          date_from: range.from || null,
          date_to: range.to || null,
        }), { withCredentials: true, headers: csrfHeaders() });
      }
    });
  };

  const handlePresetChip = (preset, value) => {
    setPresetChips((prev) => ({ ...prev, [preset.key]: value }));
    const rule = ruleByPreset[preset.key];
    if (rule && rule.active !== false && Number(rule.adjustmentValue) !== value) {
      return savePreset(preset, async () => {
        await axios.put(`${API_BASE}/api/price-rules/${rule.id}`, ruleToPayload(rule, { adjustment_value: value }), {
          withCredentials: true, headers: csrfHeaders(),
        });
      });
    }
    return undefined;
  };

  const customDraft = (preset, chipValue) => {
    if (presetCustom[preset.key] !== undefined) return presetCustom[preset.key];
    return preset.chips.includes(chipValue) ? '' : String(Math.abs(chipValue)).replace('.', ',');
  };

  const commitCustom = (preset, chipValue) => {
    const tekst = presetCustom[preset.key];
    if (tekst === undefined) return undefined;
    setPresetCustom((prev) => { const next = { ...prev }; delete next[preset.key]; return next; });
    if (String(tekst).trim() === '') return undefined;
    const wynik = parseCustomPercent(tekst, preset);
    if (wynik.error) {
      setPresetError(wynik.error === 'range'
        ? t('priceRules.presets.customRange').replace('{max}', String(wynik.max))
        : t('priceRules.presets.customInvalid'));
      return undefined;
    }
    setPresetError('');
    if (wynik.value === chipValue) return undefined;
    return handlePresetChip(preset, wynik.value);
  };

  const openNewForm = () => {
    setEditingId(null);
    setForm(emptyForm);
    setFormError('');
    setFormOpen(true);
  };

  const openEditForm = (rule) => {
    setEditingId(rule.id);
    setForm(ruleToForm(rule));
    setFormError('');
    setFormOpen(true);
  };

  // Accept = prefill the existing rule form with the suggested payload; the owner
  // reviews/edits and saves through the normal POST /api/price-rules path.
  const acceptSuggestion = (s) => {
    const sr = s.suggestedRule || {};
    setEditingId(null);
    setForm({
      name: sr.name || '',
      active: true,
      date_from: '',
      date_to: '',
      weekdays: [],
      class_id: '',
      type_id: sr.type_id != null ? String(sr.type_id) : '',
      adjustment_type: sr.adjustment_type || 'percent',
      adjustment_value: sr.adjustment_value != null ? String(sr.adjustment_value) : '',
      priority: sr.priority || 0,
      includes_bundles: false,
    });
    setFormError('');
    setFormOpen(true);
  };

  const dismissSuggestion = (key) => {
    setSuggestions((prev) => prev.filter((s) => s.key !== key));
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingId(null);
    setForm(emptyForm);
    setFormError('');
  };

  const toggleWeekday = (day) => {
    setForm((prev) => ({
      ...prev,
      weekdays: prev.weekdays.includes(day)
        ? prev.weekdays.filter((d) => d !== day)
        : [...prev.weekdays, day].sort((a, b) => a - b),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    const name = form.name.trim();
    if (!name) { setFormError(t('priceRules.nameRequired')); return; }

    const adjustmentValue = Number(form.adjustment_value);
    if (!Number.isFinite(adjustmentValue) || form.adjustment_value === '') {
      setFormError(t('priceRules.valueRequired'));
      return;
    }
    // Ta sama granica co na serwerze (routes/pricing.js): rabat ponad 100% to ujemna cena.
    if (form.adjustment_type === 'percent' && adjustmentValue < MIN_PROCENT_REGULY) {
      setFormError(t('priceRules.percentTooLow'));
      return;
    }

    if (form.date_from && form.date_to && form.date_from > form.date_to) {
      setFormError(t('priceRules.dateRangeInvalid'));
      return;
    }

    const payload = {
      name,
      active: form.active,
      date_from: form.date_from || null,
      date_to: form.date_to || null,
      weekdays: form.weekdays.length ? form.weekdays : null,
      class_id: form.class_id ? Number(form.class_id) : null,
      type_id: form.type_id ? Number(form.type_id) : null,
      adjustment_type: form.adjustment_type,
      adjustment_value: adjustmentValue,
      priority: Number(form.priority) || 0,
      applies_to: form.applies_to || 'all',
      includes_bundles: moznaNaPakiety(form) && form.includes_bundles === true,
    };

    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API_BASE}/api/price-rules/${editingId}`, payload, {
          withCredentials: true, headers: csrfHeaders(),
        });
      } else {
        await axios.post(`${API_BASE}/api/price-rules`, payload, {
          withCredentials: true, headers: csrfHeaders(),
        });
      }
      closeForm();
      loadRules();
    } catch (err) {
      setFormError(err?.response?.data?.message || t('priceRules.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (rule) => {
    const ok = await confirm({ title: t('priceRules.deleteConfirm'), tone: 'danger' });
    if (!ok) return;
    try {
      await axios.delete(`${API_BASE}/api/price-rules/${rule.id}`, {
        withCredentials: true, headers: csrfHeaders(),
      });
      loadRules();
    } catch (err) {
      setError(err?.response?.data?.message || t('priceRules.deleteError'));
    }
  };

  const formatDateRange = (rule) => {
    if (!rule.dateFrom && !rule.dateTo) return t('priceRules.allDates');
    return `${rule.dateFrom || '…'} – ${rule.dateTo || '…'}`;
  };

  const formatWeekdays = (rule) => {
    if (!Array.isArray(rule.weekdays) || !rule.weekdays.length) return t('priceRules.allDays');
    return rule.weekdays.map((d) => t(`priceRules.weekday.${d}`)).join(', ');
  };

  const formatScope = (rule) => {
    const parts = [];
    if (rule.classId != null) parts.push(classById[rule.classId] || `#${rule.classId}`);
    if (rule.typeId != null) parts.push(typeById[rule.typeId] || `#${rule.typeId}`);
    const zakres = parts.length ? parts.join(' / ') : `${t('priceRules.anyClass')} / ${t('priceRules.anyType')}`;
    // 070: reguła tylko dla klas w widgecie albo tylko dla sprzętu mówi to w tabeli.
    if (rule.appliesTo === 'classes') return `${zakres} · ${t('priceRules.scopeClassesTag')}`;
    if (rule.appliesTo === 'equipment') return `${zakres} · ${t('priceRules.scopeEquipmentTag')}`;
    return zakres;
  };

  const formatAdjustment = (rule) => {
    if (rule.adjustmentType === 'fixed') return `${rule.adjustmentValue} zł/dzień`;
    const sign = rule.adjustmentValue > 0 ? '+' : '';
    return `${sign}${rule.adjustmentValue}%`;
  };

  return (
    <PanelContainer>
      <PageHeader>
        <TitleRow>
          <IconButton onClick={() => navigate('/profile')} aria-label={t('common.backToProfile')}>
            <ArrowLeftIcon />
          </IconButton>
          <div>
            <Title>{t('priceRules.title')}</Title>
            <Subtitle>{t('priceRules.subtitle')}</Subtitle>
          </div>
        </TitleRow>
        <HeaderActions data-tour="price-new">
          <PrimaryButton data-cy="price-rules-new" onClick={openNewForm}>{t('priceRules.newRule')}</PrimaryButton>
        </HeaderActions>
      </PageHeader>
      <TylkoOdczytWPrzerwie>

      {error && <ErrorBanner>{error}</ErrorBanner>}

      {/* data-tour: kotwica mini-przewodnika strony (utils/pageTours.js) */}
      <QuickRulesCard data-cy="quick-rules" data-tour="price-presets">
        <FormTitle>{t('priceRules.presets.title')}</FormTitle>
        <FormHint>{t('priceRules.presets.hint')}</FormHint>
        {presetError && <ErrorBanner data-cy="quick-rules-error">{presetError}</ErrorBanner>}
        {PRICE_PRESETS.map((preset) => {
          const rule = ruleByPreset[preset.key];
          const isOn = !!rule && rule.active !== false;
          const chipValue = presetChipValue(preset);
          // Blokada także w trakcie ładowania listy: toggle przed fetchem reguł
          // mógłby zrobić POST duplikatu presetu (409) zamiast przełączyć istniejącą.
          const busy = presetSaving === preset.key || loading;
          const label = t(`priceRules.presets.${preset.key}.label`);
          const range = preset.hasEditableDates ? presetDateRange(preset) : null;
          const rangeLabel = range && range.from && range.to
            ? `${formatDdMm(range.from)}–${formatDdMm(range.to)}`
            : null;
          const statusText = isOn
            ? (rangeLabel
                ? t('priceRules.presets.onWithRange')
                    .replace('{value}', formatSignedPercent(Number(rule.adjustmentValue)))
                    .replace('{range}', rangeLabel)
                : t(`priceRules.presets.${preset.key}.on`).replace('{value}', formatSignedPercent(Number(rule.adjustmentValue))))
            : t(`priceRules.presets.${preset.key}.off`);
          return (
            <PresetRow key={preset.key} data-cy={`price-preset-${preset.key}`}>
              <PresetInfo>
                <strong>{label}</strong>
                <span data-cy={`price-preset-status-${preset.key}`}>{statusText}</span>
              </PresetInfo>
              {preset.hasEditableDates && (
                <PresetDateRange data-cy={`price-preset-dates-${preset.key}`}>
                  <input
                    type="date"
                    value={range.from}
                    max={range.to || undefined}
                    disabled={busy}
                    aria-label={t('priceRules.presets.dateFromAria').replace('{name}', label)}
                    onChange={(e) => handlePresetDateChange(preset, 'from', e.target.value)}
                  />
                  <span>–</span>
                  <input
                    type="date"
                    value={range.to}
                    min={range.from || undefined}
                    disabled={busy}
                    aria-label={t('priceRules.presets.dateToAria').replace('{name}', label)}
                    onChange={(e) => handlePresetDateChange(preset, 'to', e.target.value)}
                  />
                </PresetDateRange>
              )}
              <ChipGroup role="group" aria-label={t('priceRules.presets.chipsAria').replace('{name}', label)}>
                {preset.chips.map((chip) => (
                  <WeekdayChip
                    key={chip}
                    type="button"
                    data-cy={`price-preset-chip-${preset.key}-${chip}`}
                    $active={chipValue === chip}
                    aria-pressed={chipValue === chip}
                    aria-label={t('priceRules.presets.chipAria')
                      .replace('{name}', label)
                      .replace('{value}', formatSignedPercent(chip))}
                    disabled={busy}
                    onClick={() => handlePresetChip(preset, chip)}
                  >
                    {formatSignedPercent(chip)}
                  </WeekdayChip>
                ))}
                {/* Własny procent obok chipów: znak wynika z presetu (dopłata albo rabat), więc
                    wpisuje się samą liczbę. Zatwierdza Enter albo wyjście z pola. */}
                <CustomPercent
                  $active={!preset.chips.includes(chipValue)}
                  $disabled={busy}
                  data-cy={`price-preset-custom-${preset.key}`}
                >
                  <span aria-hidden="true">{presetDirection(preset) < 0 ? '−' : '+'}</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    maxLength={6}
                    // Szerokość pola rośnie z treścią, żeby znak i % stały przy liczbie.
                    style={{ width: `${Math.max(String(customDraft(preset, chipValue) || t('priceRules.presets.customPlaceholder')).length, 2) + 0.6}ch` }}
                    value={customDraft(preset, chipValue)}
                    placeholder={t('priceRules.presets.customPlaceholder')}
                    aria-label={t('priceRules.presets.customAria').replace('{name}', label)}
                    disabled={busy}
                    data-cy={`price-preset-custom-input-${preset.key}`}
                    onChange={(e) => setPresetCustom((prev) => ({ ...prev, [preset.key]: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
                      if (e.key === 'Escape') {
                        setPresetCustom((prev) => { const next = { ...prev }; delete next[preset.key]; return next; });
                      }
                    }}
                    onBlur={() => commitCustom(preset, chipValue)}
                  />
                  <span aria-hidden="true">%</span>
                </CustomPercent>
              </ChipGroup>
              <PresetSwitches>
                {/* N.2: osobno od włącznika presetu. Gdy reguły jeszcze nie ma, wybór czeka na jej założenie. */}
                <PresetBundles>
                  <span>{t('priceRules.presets.bundlesLabel')}</span>
                  <ToggleSwitch
                    type="button"
                    role="switch"
                    aria-checked={presetIncludesBundles(preset)}
                    aria-label={t('priceRules.presets.bundlesAria').replace('{name}', label)}
                    data-cy={`price-preset-bundles-${preset.key}`}
                    disabled={busy}
                    $on={presetIncludesBundles(preset)}
                    onClick={() => handlePresetBundles(preset)}
                  />
                </PresetBundles>
                <PresetBundles>
                  <span>{t('priceRules.presets.toggleLabel')}</span>
                  <ToggleSwitch
                    type="button"
                    role="switch"
                    aria-checked={isOn}
                    aria-label={t('priceRules.presets.toggleAria').replace('{name}', label)}
                    data-cy={`price-preset-toggle-${preset.key}`}
                    disabled={busy}
                    $on={isOn}
                    onClick={() => handlePresetToggle(preset)}
                  />
                </PresetBundles>
              </PresetSwitches>
            </PresetRow>
          );
        })}
      </QuickRulesCard>

      {suggestions.length > 0 && (
        <SuggestionsCard data-cy="price-suggestions">
          <FormTitle>{t('priceSuggestions.title')}</FormTitle>
          <FormHint>{t('priceSuggestions.hint')}</FormHint>
          {suggestions.map((s) => (
            <SuggestionRow key={s.key} data-cy="price-suggestion">
              <SuggestionInfo>
                <strong>{s.typeName}</strong>
                <span>
                  {s.reason === 'high_utilization' ? t('priceSuggestions.reasonHigh') : t('priceSuggestions.reasonLow')}
                  {' - '}
                  {t('priceSuggestions.utilization')}: {s.utilizationPct}%
                </span>
              </SuggestionInfo>
              <SuggestionAdjustment $positive={s.suggestedRule?.adjustment_value > 0}>
                {s.suggestedRule?.adjustment_value > 0 ? '+' : ''}{s.suggestedRule?.adjustment_value}%
              </SuggestionAdjustment>
              <RowActions>
                <PrimaryButton type="button" data-cy="price-suggestion-accept" onClick={() => acceptSuggestion(s)}>
                  {t('priceSuggestions.accept')}
                </PrimaryButton>
                <SecondaryButton type="button" data-cy="price-suggestion-dismiss" onClick={() => dismissSuggestion(s.key)}>
                  {t('priceSuggestions.dismiss')}
                </SecondaryButton>
              </RowActions>
            </SuggestionRow>
          ))}
        </SuggestionsCard>
      )}

      {formOpen && (
        <FormCard ref={formRef} onSubmit={handleSubmit}>
          <FormTitle>{editingId ? t('priceRules.editRule') : t('priceRules.newRule')}</FormTitle>

          {formError && <ErrorBanner>{formError}</ErrorBanner>}

          <FormRow>
            <FormField style={{ flex: 2 }}>
              <FormLabel>{t('priceRules.name')}</FormLabel>
              <FormInput
                data-cy="price-rules-name"
                value={form.name}
                placeholder={t('priceRules.namePlaceholder')}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              />
            </FormField>
            <FormField>
              <FormLabel>&nbsp;</FormLabel>
              <CheckboxRow>
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setForm((p) => ({ ...p, active: e.target.checked }))}
                />
                {t('priceRules.active')}
              </CheckboxRow>
            </FormField>
          </FormRow>

          <FormRow>
            <FormField>
              <FormLabel>{t('priceRules.dateFrom')}</FormLabel>
              <FormInput
                type="date"
                value={form.date_from}
                onChange={(e) => setForm((p) => ({ ...p, date_from: e.target.value }))}
              />
            </FormField>
            <FormField>
              <FormLabel>{t('priceRules.dateTo')}</FormLabel>
              <FormInput
                type="date"
                value={form.date_to}
                onChange={(e) => setForm((p) => ({ ...p, date_to: e.target.value }))}
              />
            </FormField>
          </FormRow>
          <FormHint>{t('priceRules.dateHint')}</FormHint>

          <FormField>
            <FormLabel>{t('priceRules.weekdays')}</FormLabel>
            <WeekdayRow>
              {WEEKDAYS.map((day) => (
                <WeekdayChip
                  key={day}
                  type="button"
                  $active={form.weekdays.includes(day)}
                  onClick={() => toggleWeekday(day)}
                >
                  {t(`priceRules.weekday.${day}`)}
                </WeekdayChip>
              ))}
            </WeekdayRow>
            <FormHint>{t('priceRules.weekdaysHint')}</FormHint>
          </FormField>

          <FormField>
            <FormLabel>{t('priceRules.appliesTo')}</FormLabel>
            <SimpleSelect
              style={{ width: '100%' }}
              data-cy="price-rules-applies-to"
              value={form.applies_to}
              onChange={(val) => setForm((p) => ({ ...p, applies_to: val }))}
              options={[
                { value: 'all', label: t('priceRules.appliesToAll') },
                { value: 'equipment', label: t('priceRules.appliesToEquipment') },
                { value: 'classes', label: t('priceRules.appliesToClasses') },
              ]}
            />
            <FormHint>{t('priceRules.appliesToHint')}</FormHint>
          </FormField>

          <FormRow>
            <FormField>
              <FormLabel>{t('priceRules.class')}</FormLabel>
              <SimpleSelect
                style={{ width: '100%' }}
                value={form.class_id}
                onChange={(val) => setForm((p) => ({ ...p, class_id: val }))}
                options={[
                  { value: '', label: t('priceRules.anyClass') },
                  ...classes.map((c) => ({ value: c.id, label: c.name })),
                ]}
              />
            </FormField>
            <FormField>
              <FormLabel>{t('priceRules.type')}</FormLabel>
              <SimpleSelect
                style={{ width: '100%' }}
                value={form.type_id}
                onChange={(val) => setForm((p) => ({ ...p, type_id: val }))}
                options={[
                  { value: '', label: t('priceRules.anyType') },
                  ...types.map((tp) => ({ value: tp.id, label: tp.name })),
                ]}
              />
            </FormField>
          </FormRow>

          <FormRow>
            <FormField>
              <FormLabel>{t('priceRules.adjustmentType')}</FormLabel>
              <SimpleSelect
                style={{ width: '100%' }}
                data-cy="price-rules-adjustment-type"
                value={form.adjustment_type}
                onChange={(val) => setForm((p) => ({ ...p, adjustment_type: val }))}
                options={[
                  { value: 'percent', label: t('priceRules.percent') },
                  { value: 'fixed', label: t('priceRules.fixed') },
                ]}
              />
            </FormField>
            <FormField>
              <FormLabel>{t('priceRules.adjustmentValue')}</FormLabel>
              <FormInput
                data-cy="price-rules-adjustment-value"
                type="number"
                step="0.01"
                value={form.adjustment_value}
                onChange={(e) => setForm((p) => ({ ...p, adjustment_value: e.target.value }))}
              />
              <FormHint>{t('priceRules.adjustmentValueHint')}</FormHint>
            </FormField>
            <FormField>
              <FormLabel>{t('priceRules.priority')}</FormLabel>
              <FormInput
                data-cy="price-rules-priority"
                type="number"
                value={form.priority}
                onChange={(e) => setForm((p) => ({ ...p, priority: e.target.value }))}
              />
              <FormHint>{t('priceRules.priorityHint')}</FormHint>
            </FormField>
          </FormRow>

          {/* N.2: reguła stała albo zawężona nie działa na pakiety, więc pole mówi to, zamiast znikać. */}
          <FormField>
            <CheckboxRow>
              <input
                type="checkbox"
                data-cy="price-rules-includes-bundles"
                checked={moznaNaPakiety(form) && form.includes_bundles === true}
                disabled={!moznaNaPakiety(form)}
                onChange={(e) => setForm((p) => ({ ...p, includes_bundles: e.target.checked }))}
              />
              {t('priceRules.includesBundles')}
            </CheckboxRow>
            <FormHint data-cy="price-rules-includes-bundles-hint">
              {moznaNaPakiety(form) ? t('priceRules.includesBundlesHint') : t('priceRules.includesBundlesLocked')}
            </FormHint>
          </FormField>

          <FormActions>
            <SecondaryButton type="button" onClick={closeForm}>{t('priceRules.cancel')}</SecondaryButton>
            <PrimaryButton type="submit" data-cy="price-rules-save" disabled={saving}>{t('priceRules.save')}</PrimaryButton>
          </FormActions>
        </FormCard>
      )}

      {!loading && rules.length === 0 && !formOpen && (
        <EmptyState>
          <p>{t('priceRules.empty')}</p>
          <p>{t('priceRules.emptyHint')}</p>
        </EmptyState>
      )}

      {rules.length > 0 && (
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <th>{t('priceRules.name')}</th>
                <th>{t('priceRules.columnDateRange')}</th>
                <th>{t('priceRules.columnWeekdays')}</th>
                <th>{t('priceRules.columnScope')}</th>
                <th>{t('priceRules.columnAdjustment')}</th>
                <th>{t('priceRules.columnPriority')}</th>
                <th>{t('priceRules.columnActions')}</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((rule) => (
                <tr key={rule.id} style={{ opacity: rule.active ? 1 : 0.5 }}>
                  <td>
                    {rule.name}
                    {rule.presetKey && <PresetBadge>{t('priceRules.presets.badge')}</PresetBadge>}
                    {rule.includesBundles && <PresetBadge data-cy="price-rule-bundles-badge">{t('priceRules.bundlesBadge')}</PresetBadge>}
                    {!rule.active && <InactiveNote>{t('priceRules.presets.inactive')}</InactiveNote>}
                  </td>
                  <td>{formatDateRange(rule)}</td>
                  <td>{formatWeekdays(rule)}</td>
                  <td>{formatScope(rule)}</td>
                  <td>{formatAdjustment(rule)}</td>
                  <td>{rule.priority}</td>
                  <td>
                    <RowActions>
                      <SecondaryButton onClick={() => openEditForm(rule)}>{t('priceRules.edit')}</SecondaryButton>
                      <SecondaryButton onClick={() => handleDelete(rule)}>{t('priceRules.delete')}</SecondaryButton>
                    </RowActions>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      )}
      </TylkoOdczytWPrzerwie>
    </PanelContainer>
  );
}

const ErrorBanner = styled.div`
  padding: 12px 16px;
  border-radius: 10px;
  margin-bottom: 16px;
  background: var(--danger-bg);
  border: 1px solid var(--danger-border);
  color: var(--danger);
  font-size: 0.875rem;
`;

const EmptyState = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: var(--text-muted);
  border: 1px dashed var(--input-border);
  border-radius: 16px;
`;

const FormCard = styled.form`
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
`;

const FormTitle = styled.h2`
  margin: 0 0 16px;
  font-size: 1.1rem;
  color: var(--text);
`;

const FormRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 8px;
  flex-wrap: wrap;
`;

const FormField = styled.div`
  flex: 1;
  min-width: 160px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 8px;
`;

const FormLabel = styled.label`
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text);
`;

const FormHint = styled.span`
  font-size: 0.75rem;
  color: var(--text-muted);
  margin-bottom: 8px;
  display: block;
`;

const FormInput = styled.input`
  padding: 9px 12px;
  border: 1px solid var(--input-border);
  border-radius: 8px;
  background: var(--input-bg);
  color: var(--text);
  font-size: 0.9rem;
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  color: var(--text);
  padding: 9px 0;
`;

const WeekdayRow = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const WeekdayChip = styled.button`
  padding: 6px 12px;
  border-radius: 20px;
  border: 1.5px solid ${(p) => (p.$active ? 'var(--primary)' : 'var(--input-border)')};
  background: ${(p) => (p.$active ? 'var(--primary)' : 'var(--card-bg)')};
  color: ${(p) => (p.$active ? 'white' : 'var(--text)')};
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 16px;
`;

const TableWrap = styled.div`
  overflow-x: auto;

  /* Przypieta pierwsza kolumna na ekranach dotykowych (V.6). Tabela jest szersza niz telefon,
     wiec po przewinieciu w bok bez tego nie wiadomo, ktorego wiersza dotycza widoczne kolumny.
     Zadna kolumna nie znika, przewijanie dziala jak dotad. Tlo musi byc nieprzezroczyste,
     inaczej przewijana tresc przeswituje pod spodem. Warunkiem jest DOTYK, nie sama szerokosc:
     tablet w poziomie ma 1024px i przy samym max-width zostalby bez przypiecia. */
  @media (max-width: 768px), (hover: none) and (pointer: coarse) {
    th:first-child,
    td:first-child {
      position: sticky;
      left: 0;
      z-index: 3;
      background: var(--card-bg);
      box-shadow: 6px 0 8px -6px var(--shadow-color);
    }
  }

  border: 1px solid var(--input-border);
  border-radius: 16px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: 12px 14px;
    text-align: left;
    font-size: 0.85rem;
    border-bottom: 1px solid var(--input-border);
    color: var(--text);
    white-space: nowrap;
  }

  /* Kolumny mają bardzo różne szerokości (auto-layout dopasowuje je do najszerszej treści —
     "Zakres sprzętu" vs "Priorytet"), przez co lewo wyrównany nagłówek siedział przy lewej
     krawędzi wąskiej kolumny, a dane pod nim (np. samo "0") wyglądały na zagubione w pustej
     przestrzeni. Wyśrodkowanie SAMYCH nagłówków (dane zostają z lewej — czytelniejsze dla
     tekstu zmiennej długości) spaja wizualnie każdą kolumnę z jej etykietą. */
  th {
    background: var(--table-header-bg);
    font-weight: 700;
    text-align: center;
  }

  /* Ostatnia kolumna (Akcje) kończyła się tuż przy prawej krawędzi tabeli — przycisk "Usuń"
     dotykał ramki. Dodatkowy odstęp z prawej WYŁĄCZNIE na tej kolumnie, żeby reszta (gęściej
     upakowana) nie zrobiła się przez to nierówna. */
  th:last-child, td:last-child {
    padding-right: 24px;
  }

  tbody tr:last-child td { border-bottom: none; }
`;

const RowActions = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
`;

const SuggestionsCard = styled.div`
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
`;

const SuggestionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 0;
  border-top: 1px solid var(--input-border);
  flex-wrap: wrap;

  &:last-child { padding-bottom: 0; }
`;

const SuggestionInfo = styled.div`
  flex: 1;
  min-width: 200px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  color: var(--text);
  font-size: 0.9rem;

  span { color: var(--text-muted); font-size: 0.8rem; }
`;

const SuggestionAdjustment = styled.div`
  font-weight: 700;
  font-size: 1rem;
  color: ${(p) => (p.$positive ? 'var(--success)' : 'var(--danger)')};
  min-width: 56px;
  text-align: right;
`;

const QuickRulesCard = styled(SuggestionsCard)``;

const PresetRow = styled(SuggestionRow)``;

const PresetInfo = styled(SuggestionInfo)``;

// Zakres dat presetu (sezon wysoki / poza sezonem) — edytowalny wprost w wierszu presetu,
// żeby nie trzeba było otwierać osobnego formularza tylko po to, żeby zmienić dwie daty.
// Na telefonie pole daty jest szersze (pismo 16 px), więc oba pola nie mieszczą się w karcie
// w jednym rzędzie: drugie pole schodzi niżej. Szerokość z treści pola, żeby daty nie były przycięte,
// a flex-shrink: 0, bo inaczej wiersz presetu ściskałby zakres i zawijał go także na komputerze.
const PresetDateRange = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  max-width: 100%;

  input[type="date"] {
    flex: 1 1 auto;
    min-width: 0;
    box-sizing: border-box;
    padding: 6px 8px;
    border: 1px solid var(--input-border);
    border-radius: 8px;
    background: var(--input-bg);
    color: var(--text);
    font-size: 0.8rem;
    /* Bez tego natywna ikona kalendarza w Chrome/Edge rysuje się w kolorze dla jasnego
       motywu — w trybie ciemnym wychodzi ciemna ikona na ciemnym tle, praktycznie niewidoczna. */
    color-scheme: light dark;
  }

  span { color: var(--text-muted); font-size: 0.8rem; }
`;

const ChipGroup = styled(WeekdayRow)`
  align-items: center;
`;

// Włączniki presetu trzymają się razem, także gdy wiersz zawinie się na węższym ekranie.
const PresetSwitches = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  flex-shrink: 1;
  max-width: 100%;
  margin-left: auto;
`;

// Przełączniki presetu: oba z podpisem, bo stoją obok siebie i wyglądają tak samo.
const PresetBundles = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8rem;
  color: var(--text-muted);
`;

// Pole „własny” procent wygląda jak czwarty chip: ta sama wysokość, obrys i stan aktywny.
// Aktywne (wypełnione), gdy obowiązująca wartość nie jest żadnym z gotowych chipów.
const CustomPercent = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 1px;
  padding: 0 12px;
  height: 30px;
  box-sizing: border-box;
  border-radius: 20px;
  border: 1.5px solid ${(p) => (p.$active ? 'var(--primary)' : 'var(--input-border)')};
  background: ${(p) => (p.$active ? 'var(--primary)' : 'var(--card-bg)')};
  color: ${(p) => (p.$active ? 'white' : 'var(--text-muted)')};
  font-size: 0.8rem;
  font-weight: 600;
  cursor: text;
  opacity: ${(p) => (p.$disabled ? 0.6 : 1)};

  &:focus-within {
    outline: 2px solid var(--primary);
    outline-offset: 2px;
  }

  input {
    border: 0;
    padding: 0;
    background: transparent;
    color: ${(p) => (p.$active ? 'white' : 'var(--text)')};
    font: inherit;
    text-align: right;
    outline: none;

    &::placeholder {
      color: var(--text-muted);
      font-weight: 500;
    }
  }
`;

const PresetBadge = styled.span`
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 10px;
  background: rgba(59, 130, 246, 0.12);
  color: var(--primary);
  font-size: 0.7rem;
  font-weight: 700;
  white-space: nowrap;
`;

const InactiveNote = styled.span`
  display: inline-block;
  margin-left: 8px;
  color: var(--text-muted);
  font-size: 0.7rem;
  font-weight: 600;
  white-space: nowrap;
`;
